import type { PropsWithChildren } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "../auth/sessionContext";
import { InlineNotice } from "../components/Feedback/InlineNotice";
import {
  hasSimpleBillingAccess,
  pathAllowsReadOnlySimpleBilling,
  roleCanAccessSimpleBillingPath,
  simpleBillingAccessState,
} from "./simpleBillingAccess";
import styles from "./SimpleBillingShell.module.css";

function accessDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
}

export function SimpleBillingRouteGuard({ children }: PropsWithChildren) {
  const session = useSession();
  const location = useLocation();
  const accessState = simpleBillingAccessState(session);

  if (!hasSimpleBillingAccess(session)) {
    if (session.workspaceProduct === "SIMPLE_BILLING" && session.user.role !== "super_admin") {
      return (
        <section className={styles.denied} aria-label="Billing subscription required">
          <InlineNotice tone="info" title="Activate your Billing subscription">
            Your Billing workspace is ready. Review the subscription to unlock bill creation, products, PDFs, and reports.{" "}
            <Link to="/subscription">Review subscription</Link>
          </InlineNotice>
        </section>
      );
    }
    return (
      <section className={styles.denied} aria-label="Billing unavailable">
        <InlineNotice tone="error" title="Billing unavailable">
          Simple Billing is not available for the selected workspace.
        </InlineNotice>
      </section>
    );
  }

  if (!roleCanAccessSimpleBillingPath(session.user.role, location.pathname)) {
    return (
      <section className={styles.denied} aria-label="Billing access restricted">
        <InlineNotice tone="error" title="Billing access restricted">
          Your current role cannot open this Billing screen.
        </InlineNotice>
      </section>
    );
  }

  if (accessState === "read_only" && !pathAllowsReadOnlySimpleBilling(location.pathname)) {
    return (
      <section className={styles.denied} aria-label="Billing workspace read-only">
        <InlineNotice tone="warning" title="Billing workspace is read-only">
          New bills, edits, products, rates, settings, and Team changes are paused. Historical bills, PDFs, and reports remain available.{" "}
          <Link to="/subscription">Reactivate subscription</Link>
        </InlineNotice>
      </section>
    );
  }

  const graceEnd = accessDate(session.simpleBillingAccess?.graceEndsAt);
  const periodEnd = accessDate(session.simpleBillingAccess?.currentPeriodEnd);
  const lifecycleNotice = accessState === "grace" ? (
    <InlineNotice tone="warning" title="Payment recovery in progress">
      Full Billing access remains available during the three-day grace period{graceEnd ? ` through ${graceEnd}` : ""}. Razorpay will retry the payment.{" "}
      <Link to="/subscription">Review subscription</Link>
    </InlineNotice>
  ) : accessState === "read_only" ? (
    <InlineNotice tone="warning" title="Historical access only">
      Your subscription is inactive. Bills, PDFs, and reports remain available, but all Billing and Team changes are paused.{" "}
      <Link to="/subscription">Reactivate subscription</Link>
    </InlineNotice>
  ) : session.simpleBillingAccess?.reason === "cancellation_scheduled" ? (
    <InlineNotice tone="info" title="Cancellation scheduled">
      Full Billing access continues{periodEnd ? ` through ${periodEnd}` : " until the current billing cycle ends"}.{" "}
      <Link to="/subscription">Review subscription</Link>
    </InlineNotice>
  ) : null;

  return <div className={styles.accessBoundary}>{lifecycleNotice}{children}</div>;
}
